import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import React, { useEffect, useState, useRef } from 'react';
import { type RouteComponentProps, withRouter } from 'react-router-dom';
import { GenericDetailedError } from '~/errors/GenericDetailedError';
import { type ExportEntry, ExportService, type ExportsYear } from '~/services/exports';

interface MatchProps {
  id: string;
}

interface ExistingExportsProps extends RouteComponentProps<MatchProps> {
  onError: (error: GenericDetailedError) => void;
  onDelete: (exported: ExportEntry) => Promise<boolean>;
}

const ExistingExports = ({ match, onError, onDelete }: ExistingExportsProps) => {
  const [currentExports, setCurrentExports] = useState<ExportsYear[]>([]);
  const highlightRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    const currentExportsSub = ExportService.exports().subscribe((exports) => {
      setCurrentExports(exports);
    });

    return () => currentExportsSub.unsubscribe();
  }, []);

  useEffect(() => {
    if (match.params.id) {
      if (highlightRef.current) {
        highlightRef.current.scrollIntoView({ behavior: 'smooth' });
      } else {
        onError(
          new GenericDetailedError(
            'Exporten finns inte',
            'Exporten du försökte ladda ned finns inte, eller så har du inte rättigheter att se den.'
          )
        );
      }
    }
  }, [match.params.id, currentExports]);

  const remove = (accepted: boolean, index: number, entryToRemove: ExportEntry) => {
    if (!accepted) return;

    const updatedExports = [...currentExports];
    updatedExports[index].exports = updatedExports[index].exports.filter((x) => x.id !== entryToRemove.id);
    setCurrentExports(updatedExports);
  };

  return (
    <Table data-testid={'table'}>
      <TableHead>
        <TableRow>
          <TableCell>Submissionsår</TableCell>
          <TableCell>Version</TableCell>
          <TableCell>Exporttyp</TableCell>
          <TableCell>Exportfil</TableCell>
          <TableCell>Sekretess</TableCell>
          <TableCell>Skapad</TableCell>
          <TableCell>Radera</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {currentExports.map((year, index) => (
          <React.Fragment key={index}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  verticalAlign: 'top',
                  paddingTop: '18px',
                }}
                rowSpan={year.exports.length + 1}
              >
                {year.year}
              </TableCell>
            </TableRow>
            {year.exports
              .sort((a: ExportEntry, b: ExportEntry) => {
                if (a.createdAt && b.createdAt) {
                  return b.createdAt.getTime() - a.createdAt.getTime();
                }
                return b.filename.localeCompare(a.filename);
              })
              .map((entry, entryIndex) => {
                const isHighlighted = entry.id.toString() === match.params.id;
                return (
                  <TableRow
                    key={entryIndex}
                    data-testid={`export${entry.id}`}
                    ref={isHighlighted ? highlightRef : null}
                    sx={isHighlighted ? { backgroundColor: '#eaeaea' } : undefined}
                  >
                    <TableCell>{entry.version}</TableCell>
                    <TableCell>{entry.exportType}</TableCell>
                    <TableCell>
                      <a href={`/api/submissions/${entry.id}`} download={entry.filename}>
                        {entry.filename}
                      </a>
                    </TableCell>
                    <TableCell>{entry.secrecy ? 'Ja' : 'Nej'}</TableCell>
                    <TableCell>{entry.createdAt ? entry.createdAt.toLocaleString() : '-'}</TableCell>
                    <TableCell>
                      <IconButton
                        sx={{ padding: '5px' }}
                        onClick={() => onDelete(entry).then((res) => remove(res, index, entry))}
                      >
                        <DeleteIcon titleAccess="Delete" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
          </React.Fragment>
        ))}
      </TableBody>
    </Table>
  );
};

export default withRouter(ExistingExports);
